import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';

import { API_URL_V4, APP_TOKEN } from 'src/environments/environment.local';
import * as filterHelpers from '../helpers/brand-filter.helpers';
import { Brand, Model, PartnerType } from '../models/brand.interface';
import { Environment } from '../models/environment.type';
import { MachineType } from '../models/machine-type.interface';

@Injectable()
export class CompatibilityService {
  private readonly _brandsUrl: string;
  private _params: HttpParams;
  private _brands: Brand[];
  readonly alphabet: string[];
  readonly machineTypes: MachineType[];
  readonly isLoadingBrands$: BehaviorSubject<boolean>;
  readonly currentStep$: BehaviorSubject<number>;
  readonly selectedEnvironment$: BehaviorSubject<Environment | null>;
  readonly selectedEquipment$: BehaviorSubject<MachineType | null>;
  readonly isOpticalSelected$: BehaviorSubject<boolean>;
  readonly scrollToId$: Subject<string>;
  private readonly _selectedFilter$: BehaviorSubject<string>;
  readonly selectedFilter$: Observable<string>;
  readonly nextFilter$: Observable<string>;
  readonly filteredBrands$: Observable<Brand[]>;
  readonly nextBrands$: Observable<Brand[]>;
  private readonly _selectedBrand$: BehaviorSubject<Brand | null>;
  readonly selectedBrand$: Observable<Brand | null>;

  readonly goldBrandPartner$: Observable<Brand[] | null>;
  readonly silverBrandPartner$: Observable<Brand[] | null>;
  readonly bronzeBrandPartner$: Observable<Brand[] | null>;

  readonly brandsPartners$: Observable<Brand[] | null>; // Une liste qui regroupe tout les Golds et Silvers Partenaires dans cette ordre : [A-goldPartner, F-goldPartner, T-goldPartner, A-silverPartner, F-silverPartner, T-silverPartner]

  constructor(private http: HttpClient) {
    this._brandsUrl = `${API_URL_V4}/manufacturer`;
    this._params = new HttpParams().set('appToken', APP_TOKEN);
    this._brands = [];
    this.alphabet = Array.from(Array(26))
      .map((e, i) => i + 65)
      .map((x) => String.fromCharCode(x).toLowerCase());
    this.machineTypes = [
      {
        id: 1,
        name: 'compatibility.machineType.hometrainer',
        icon: 'icon-hometrainer',
      },
      {
        id: 2,
        name: 'compatibility.machineType.exercice-bike',
        icon: 'icon-exercice-bike',
      },
      {
        id: 3,
        name: 'compatibility.machineType.treadmill',
        icon: 'icon-treadmill',
      },
      {
        id: 4,
        name: 'compatibility.machineType.elliptical',
        icon: 'icon-elliptical',
      },
      { id: 5, name: 'compatibility.machineType.rowing', icon: 'icon-rowing' },
    ];
    this.isLoadingBrands$ = new BehaviorSubject(false);
    this.currentStep$ = new BehaviorSubject(1);
    this.selectedEnvironment$ = new BehaviorSubject(null);
    this.selectedEquipment$ = new BehaviorSubject(null);
    this.isOpticalSelected$ = new BehaviorSubject(false);
    this.scrollToId$ = new Subject<string>();
    this._selectedFilter$ = new BehaviorSubject<string>('a');
    this.selectedFilter$ = this._selectedFilter$.asObservable();
    this.nextFilter$ = this._selectedFilter$.pipe(
      map(() => {
        return this.findNextFilter();
      })
    );
    this.filteredBrands$ = this.selectedFilter$.pipe(
      map((filter) => {
        return filterHelpers.filterBrandsByFirstCharacter(this._brands, filter);
      })
    );
    this.nextBrands$ = this.nextFilter$.pipe(
      map((filter) => {
        return filterHelpers
          .filterBrandsByFirstCharacter(this._brands, filter)
          .splice(0, 9);
      })
    );
    this._selectedBrand$ = new BehaviorSubject<Brand | null>(null);
    this.selectedBrand$ = this._selectedBrand$.asObservable();

    this.goldBrandPartner$ = this.filteredBrands$.pipe(
      map((brands) =>
        brands.filter((brand) => brand.partnership === PartnerType.GOLD)
      )
    );

    this.silverBrandPartner$ = this.filteredBrands$.pipe(
      map((brands) =>
        brands.filter((brand) => brand.partnership === PartnerType.SILVER)
      )
    );

    this.bronzeBrandPartner$ = this.filteredBrands$.pipe(
      map((brands) =>
        brands.filter((brand) => brand.partnership === PartnerType.BRONZE)
      )
    );
  }

  selectEnvironment(choice: Environment): void {
    this.goToStep(2);

    this.selectedEnvironment$.next(choice);

    if (choice === 'all') {
      this.isLoadingBrands$.next(true);
      this.getBrands().subscribe((res: Brand[]) => {
        this._brands = res;
        this.isLoadingBrands$.next(false);
      });
    }
  }

  selectEquipment(machineTypeId: number): void {
    this.goToStep(3);

    this.selectedEquipment$.next(
      this.machineTypes.find((mt) => mt.id === machineTypeId)
    );

    this.isLoadingBrands$.next(true);
    this.getBrands().subscribe((res: Brand[]) => {
      this._brands = res;
      this.isLoadingBrands$.next(false);
    });
  }

  selectOpticalIfTrue(choice: boolean): void {
    if (this.selectedEnvironment$.getValue() === 'all') {
      this.goToStep(2);
    } else {
      this.goToStep(3);
    }
    this.isOpticalSelected$.next(choice);
  }

  selectFilters(filter: string): void {
    if (
      this.alphabet.includes(filter) ||
      filter === '0-9' ||
      filter === 'PARTNER'
    ) {
      this._selectedFilter$.next(filter);
    }
  }

  selectBrand(brandId: number): void {
    if (this.selectedEnvironment$.getValue() === 'all') {
      this.goToStep(3);
    } else {
      this.goToStep(4);
    }

    this._selectedBrand$.next(
      this._brands.find((brand) => brand.id === brandId)
    );

    this.getBrandModels().subscribe((res: Model[]) => {
      this._selectedBrand$.next({
        ...this._selectedBrand$.getValue(),
        models: res,
      });
    });
  }

  goToStep(step: number): void {
    if (this.currentStep$.getValue() !== step) {
      this.currentStep$.next(step);
      if (
        (this.selectedEnvironment$.getValue() !== 'all' && step < 4) ||
        step < 3
      ) {
        this.isOpticalSelected$.next(false);
        this._selectedBrand$.next(null);
        this.selectFilters('PARTNER');
      }
      if (step < 3) {
        this.selectedEquipment$.next(null);
      }
      if (step < 2) {
        this.selectedEnvironment$.next(null);
      }
    }
    this.scrollToId$.next(`compatibilityStep${step}`);
  }

  private getBrands(): Observable<Brand[]> {
    const equipment = this.selectedEquipment$.getValue()
      ? this.selectedEquipment$.getValue().id
      : 'all';
    this._params = this._params.set('equipment', equipment);

    return this.http.get(this._brandsUrl, { params: this._params }).pipe(
      map((res: any) => {
        return res.data
          .map((item) => {
            return {
              id: item.id,
              name: item.name,
              logo: item.logo,
              models: [],
              websiteUrl: item.url,
              partnership: item.partnership,
            };
          })
          .sort((brand1: Brand, brand2: Brand) => {
            return brand1.name.toLowerCase() > brand2.name.toLowerCase()
              ? +1
              : -1;
          });
      })
    );
  }

  private getBrandModels(): Observable<Model[]> {
    const brandModelsUrl: string = `${this._brandsUrl}/${this._selectedBrand$.getValue().id
      }`;

    this._params = this._params.set(
      'plateform',
      this.selectedEnvironment$.getValue()
    );
    const equipment = this.selectedEquipment$.getValue()
      ? this.selectedEquipment$.getValue().id
      : 'all';
    this._params = this._params.set('equipment', equipment);

    return this.http.get(brandModelsUrl, { params: this._params }).pipe(
      map((res: any) => {
        return res.equipment
          .filter((equipment) => {
            return this.machineTypes.some((mt) => {
              return (
                mt.id === equipment.equ_machine_type &&
                equipment.equ_status != 0
              );
            });
          })
          .map((equipment) => {
            const isBluetoothIos: boolean = equipment.protocols.some((p) => {
              return (
                (p.pro_type_techno === 3 || p.pro_type_techno === 4) &&
                p.pro_ios === 1
              );
            });
            const isBluetoothAndroid: boolean = equipment.protocols.some(
              (p) => {
                return (
                  (p.pro_type_techno === 3 || p.pro_type_techno === 4) &&
                  p.pro_android === 1
                );
              }
            );
            const isAntPlusIos: boolean = equipment.protocols.some(
              (protocol) => {
                return protocol.pro_type_techno === 2 && protocol.pro_ios === 1;
              }
            );
            const isAntPlusAndroid: boolean = equipment.protocols.some(
              (protocol) => {
                return (
                  protocol.pro_type_techno === 2 && protocol.pro_android === 1
                );
              }
            );
            const isOpticalSensorIos: boolean = equipment.protocols.some(
              (protocol) => {
                return protocol.pro_type_techno === 7 && protocol.pro_ios === 1;
              }
            );
            const isOpticalSensorAndroid: boolean = equipment.protocols.some(
              (protocol) => {
                return (
                  protocol.pro_type_techno === 7 && protocol.pro_android === 1
                );
              }
            );
            const isEmbedded: boolean =
              equipment.protocols.some((protocol) => {
                return protocol.pro_type_techno === 10;
              }) || equipment.equ_protocols_concat.includes('embedded');

            return {
              id: equipment.equ_id,
              name: equipment.equ_model,
              machineType: this.machineTypes.find((mt: MachineType) => {
                return mt.id === equipment.equ_machine_type;
              }),
              interactivity: equipment.equ_interactivity,
              bluetoothCompatibility: {
                ios: isBluetoothIos,
                android: isBluetoothAndroid,
              },
              antPlusCompatibility: {
                ios: isAntPlusIos,
                android: isAntPlusAndroid,
              },
              opticalSensorCompatibility: {
                ios: isOpticalSensorIos,
                android: isOpticalSensorAndroid,
              },
              embeddedCompatibility: isEmbedded,
              isIos: equipment.protocols.some((p) => {
                return p.pro_ios === 1;
              }),
              isAndroid: equipment.protocols.some((p) => {
                return p.pro_android === 1;
              }),
            };
          });
      })
    );
  }

  private findNextFilter(): string | null {
    if (this._selectedFilter$.getValue() === '0-9') {
      return null;
    }

    //? Au cas où les décisions changent encore.
    // if (this._selectedFilter$.getValue() === '0-9') {
    //   return 'a';
    // }

    if (this._selectedFilter$.getValue() === 'z') {
      return '0-9';
    }

    const index = this.alphabet.indexOf(this._selectedFilter$.getValue());
    return this.alphabet[index + 1];
  }
}
